import React, { JSX, useState } from "react";
import { AccessControlKeys } from "utilities/enumerations/authorization/access-control-keys";
import { validatePageAccess } from "utilities/decorators/aspects/authorization/validate-page-access";
import { Heading, HeadingPriority, HeadingSize } from "components/typography/heading/heading";
import { SkipNavContent } from "@chakra-ui/skip-nav";
import { t } from "utilities/localization/t";
import { Button, ButtonType } from "components/buttons/button/button";
import { ProviderService } from "utilities/services/providers/provider-service";
import { useGetProviderSummary } from "data/data-access-hooks/providers/providers-queries";
import { useProvider } from "utilities/contexts/use-provider-context";
import { ButtonStyle } from "components/buttons/button/button";
import { Spinner } from "components/spinner/spinner";
import DisplayCard from "./display-card";
import ProviderEventListAccordion from "./provider-event-list-accordion";
import { sitemap } from "../../../../../sitemap";
import { ProviderFeeInvoiceRecord } from "models/view-models/providers/provider-fee-invoice-record";
import { InvoiceProductType } from "models/enumerations/providers/invoice-product-type";
import { ProviderInvoiceFeeModal } from "components/user-management/providers/provider-invoice-fee-modal/provider-fee-invoice-modal";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "data/data-access-hooks/query-keys";
import { RoleType } from "models/enumerations/users/role-type";
import { useGlobalState } from "utilities/contexts/use-global-state-context";

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

interface UserProvidersEventHistoryPageProps {}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Constants
// -------------------------------------------------------------------------------------------------

// #endregion Constants

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------

const UserProvidersEventHistoryPage: React.FC<UserProvidersEventHistoryPageProps> =
    validatePageAccess(AccessControlKeys.UserProvidersEventHistoryPage)((): JSX.Element => {
        const { record: provider } = useProvider();
        const { list: getSummaryList } = ProviderService.useGetProviderSummary();

        const { data: summaryData, isLoading: isLoadingSummary, refetch: refetchSummary } = useGetProviderSummary(
            getSummaryList,
            +(provider?.id ?? 0)
        );

        const [providerFeeInvoice, setProviderFeeInvoice] = useState<ProviderFeeInvoiceRecord>();   

        const [showAddProviderInvoiceFeeModal, setShowAddProviderInvoiceFeeModal] = useState(false);
        const showAddProviderInvoiceFeeModalOnClick = () => {
            setProviderFeeInvoice(new ProviderFeeInvoiceRecord({
                providerId: provider?.id,
                productTypeId: InvoiceProductType.AnnualFee,
            }));
            setShowAddProviderInvoiceFeeModal(true);
        };

        const queryClient = useQueryClient();
        // Each year's list owns its own query, so refetch all of this provider's invoice lists.
        const refetchProviderFeeInvoices = () =>
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.PROVIDER_FEE_INVOICE_LIST, { providerId: provider?.id }],
                refetchType: "all",
            });

        const handleAddFormSave = async (): Promise<boolean> => {
            await refetchProviderFeeInvoices();
            await refetchSummary();
            return true;
        };

        const { record: globalState } = useGlobalState();

        const isNFPAAdminOrOperator = globalState.currentIdentity?.isCurrentlyInRole(RoleType.NfpaAdministrator) ||
                globalState.currentIdentity?.isCurrentlyInRole(RoleType.NfpaOperator);

        const canAddProviderInvoiceFee = isNFPAAdminOrOperator;

        return (
            <div>
                <div className="content-wrap">
                    <div className="content">
                        <SkipNavContent>
                            <div className="flex justify-between">
                                <Heading priority={HeadingPriority.H2} size={HeadingSize.XSmall}>
                                    {t("eventHistory")}
                                </Heading>
                                <div className="flex flex-col items-end space-y-2">
                                    <Button
                                        cssClassName="!w-40"
                                        linkPath={`${
                                            sitemap.admin.event.list
                                        }?providerId=${encodeURIComponent(provider?.id ?? "")}`}
                                        text={t("goToEvents")}
                                        type={ButtonType.Link}
                                    />
                                    {canAddProviderInvoiceFee && (
                                        <Button cssClassName="!w-40" style={ButtonStyle.Secondary}
                                            onClick={() => showAddProviderInvoiceFeeModalOnClick()}>
                                            {t("addInvoice")}
                                        </Button>
                                    )}
                                    {showAddProviderInvoiceFeeModal && (
                                        <ProviderInvoiceFeeModal
                                            open={showAddProviderInvoiceFeeModal}
                                            setOpen={setShowAddProviderInvoiceFeeModal}
                                            providerFeeInvoiceToEdit={providerFeeInvoice}
                                            onEditFormSave={handleAddFormSave}
                                        />
                                    )}
                                </div>
                            </div>
                            {isLoadingSummary ? (
                                <div className="flex justify-center items-center h-80">
                                    <Spinner />
                                </div>
                            ) : (
                                <>
                                    <div className="flex space-x-4">
                                        <DisplayCard
                                            title={t("totalEvents")}
                                            value={(summaryData?.totalEvents ?? 0).toString()}
                                        />
                                        <DisplayCard
                                            title={t("totalInvoiced")}
                                            value={`$${(summaryData?.totalInvoiced ?? 0).toFixed(
                                                0
                                            )}`}
                                        />
                                    </div>
                                    <div className="mt-5">
                                        {summaryData?.years.map((summary) => (
                                            <ProviderEventListAccordion
                                                key={summary?.year}
                                                providerId={provider?.id}
                                                summary={summary}
                                            />
                                        ))}
                                    </div>
                                </>
                            )}
                        </SkipNavContent>
                    </div>
                </div>
            </div>
        );
    });

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export { UserProvidersEventHistoryPage };

// #endregion Exports
